import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Button, Modal, Tabs } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import StaffingSettingsLayout from "./components/StaffingSettingsLayout";
import { StaffingPermissionsContext } from "./components/StaffingPermissionsContext";
import RolePermissionsGrid from "./components/RolePermissionsGrid";
import PersonOverridesCard from "./components/PersonOverridesCard";
import EffectivePermissionsCard from "./components/EffectivePermissionsCard";
import PermissionGrantDrawer from "./components/PermissionGrantDrawer";
import { errorMessages } from "./components/staffingFormatters";

const VIEWS = [
  { value: "roles", label: "Roles" },
  { value: "people", label: "People" },
];

const StaffingPermissionsApp = ({ permissions, roles, members, departments, positions, grants: initialGrants, canReset, paths, tabPaths }) => {
  const [grants, setGrants] = useState(initialGrants);
  const [view, setView] = useState("roles");
  const [draft, setDraft] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const inFlight = useRef(false);
  const [mutating, setMutating] = useState(false);

  const mutate = useCallback(async (method, url, body, failureMessage) => {
    if (inFlight.current) return { ok: false, errors: [] };
    inFlight.current = true;
    setMutating(true);
    try {
      const { data } = await client[method](url, body);
      setGrants(data.grants);
      return { ok: true, errors: [] };
    } catch (error) {
      const errors = errorMessages(error);
      toastError(errors.length ? errors.join(" ") : failureMessage);
      return { ok: false, errors };
    } finally {
      inFlight.current = false;
      setMutating(false);
    }
  }, []);

  const resetToDefaults = async () => {
    setResetting(true);
    const result = await mutate("post", paths.reset, {}, "Could not reset permissions.");
    setResetting(false);
    if (result.ok) {
      setConfirmingReset(false);
      toastSuccess("Permissions are back to the defaults.");
    }
  };

  const value = useMemo(
    () => ({ permissions, roles, members, departments, positions, grants, paths, mutate }),
    [permissions, roles, members, departments, positions, grants, paths, mutate]
  );

  return (
    <StaffingPermissionsContext.Provider value={value}>
      <StaffingSettingsLayout
        current="permissions"
        title="Permissions"
        description="Choose who can see and change schedules, people, time cards and pay in Staffing."
        tabPaths={tabPaths}
        actions={
          canReset && (
            <Button variant="secondary" disabled={grants.length === 0 || mutating} onClick={() => setConfirmingReset(true)}>
              Reset to defaults
            </Button>
          )
        }
      >
        <div className="mb-3">
          <Tabs items={VIEWS} value={view} onChange={setView} />
        </div>

        {view === "roles" && <RolePermissionsGrid onEdit={setDraft} />}
        {view === "people" && (
          <>
            <PersonOverridesCard onEdit={setDraft} />
            <EffectivePermissionsCard />
          </>
        )}

        {draft && <PermissionGrantDrawer draft={draft} onClose={() => setDraft(null)} />}

        {confirmingReset && (
          <Modal
            open
            size="sm"
            title="Reset to defaults?"
            description="This removes every role and person override. Everyone goes back to their role's default staffing permissions."
            onClose={() => setConfirmingReset(false)}
            footer={
              <div className="d-flex justify-content-end w-100" style={{ gap: 8 }}>
                <Button variant="secondary" onClick={() => setConfirmingReset(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" disabled={resetting || mutating} onClick={resetToDefaults}>
                  {resetting ? "Resetting…" : "Reset permissions"}
                </Button>
              </div>
            }
          />
        )}
      </StaffingSettingsLayout>
      <ToastContainer icon={false} />
    </StaffingPermissionsContext.Provider>
  );
};

const optionShape = PropTypes.shape({
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
});

StaffingPermissionsApp.propTypes = {
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      teamWide: PropTypes.bool.isRequired,
      editable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      defaults: PropTypes.arrayOf(PropTypes.string).isRequired,
      editable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  members: PropTypes.arrayOf(optionShape).isRequired,
  departments: PropTypes.arrayOf(optionShape).isRequired,
  positions: PropTypes.arrayOf(optionShape).isRequired,
  grants: PropTypes.arrayOf(PropTypes.object).isRequired,
  canReset: PropTypes.bool.isRequired,
  paths: PropTypes.shape({
    grants: PropTypes.string.isRequired,
    reset: PropTypes.string.isRequired,
    effective: PropTypes.string.isRequired,
  }).isRequired,
  tabPaths: PropTypes.object.isRequired,
};

export default StaffingPermissionsApp;
